import type { DesignPage } from '../../services/designService';

export function designPageRevision(page: DesignPage): string {
  return `${page.version}:${page.preview_revision || page.updated_at || ''}`;
}

/** Progress can change without changing the rendered content or its timestamp. */
export function designPageProgressRevision(page: DesignPage): string {
  return JSON.stringify([
    page.generation_run_id, page.generation_in_progress, page.generation_failed,
    page.credit_blocked, page.generation_phase, page.sections_done, page.sections_total,
    page.generation_message,
  ]);
}

/** A late HTTP read must not overwrite a preview announced while it was loading. */
export function canMergeDesignPageResponse(
  current: DesignPage, response: DesignPage, requestedRevision?: string,
): boolean {
  return !requestedRevision || requestedRevision === designPageRevision(current)
    || designPageRevision(response) === designPageRevision(current);
}

/** Load matching content without letting a delayed read roll back streamed progress. */
export function mergeDesignPageContentResponse(
  current: DesignPage,
  response: DesignPage,
  requestedRevision?: string,
  requestedProgressRevision?: string,
): DesignPage {
  if (!canMergeDesignPageResponse(current, response, requestedRevision)) return current;
  const currentProgress = designPageProgressRevision(current);
  const progressChanged = requestedProgressRevision !== undefined
    && requestedProgressRevision !== currentProgress
    && designPageProgressRevision(response) !== currentProgress;

  return mergeDesignPageMetadata(current, progressChanged ? {
    ...current,
    compiled_html: response.compiled_html,
    jsx_content: response.jsx_content,
    loaded_content_revision: designPageRevision(response),
  } : response);
}

export function needsDesignPageContent(page: DesignPage): boolean {
  const loaded = Boolean(page.compiled_html || page.jsx_content);
  const available = loaded || page.has_compiled_html || page.has_jsx_content;

  return Boolean(available && (!loaded || (
    page.loaded_content_revision && page.loaded_content_revision !== designPageRevision(page)
  )));
}

function isOlderRevision(previous: DesignPage, next: DesignPage): boolean {
  if (previous.version !== next.version) return next.version < previous.version;
  if (!previous.updated_at || !next.updated_at) return false;
  const previousTime = Date.parse(previous.updated_at);
  const nextTime = Date.parse(next.updated_at);

  if (previousTime !== nextTime) return nextTime < previousTime;

  // Backend timestamps can differ by microseconds within one JS millisecond.
  return previous.updated_at.split('.')[0] === next.updated_at.split('.')[0]
    && next.updated_at < previous.updated_at;
}

function isCleanCompletedPage(page: DesignPage): boolean {
  const phase = page.generation_phase || '';
  const sectionsComplete = !page.sections_total || (page.sections_done || 0) >= page.sections_total;

  // Completion clears the backend phase and counters. An idle partial/failed
  // preview must not receive the same protection as that clean final state.
  return (phase === '' || phase === 'completed')
    && Boolean(page.has_compiled_html || page.compiled_html?.trim())
    && !page.generation_in_progress && !page.generation_failed && !page.credit_blocked
    && !page.planned && !page.id.startsWith('virtual:') && !page.id.startsWith('planned-')
    && !page.missing_sections_json?.length && sectionsComplete;
}

function isStalePublication(previous: DesignPage, next: DesignPage): boolean {
  // Some servers persist timestamps only to the second. A sibling's delayed
  // snapshot can therefore share the completed page's exact timestamp.
  // A newly started attempt has planning/generating metadata, a newer timestamp,
  // a different version/content, or a distinct retained run ID and still wins.
  return isCleanCompletedPage(previous)
    && Boolean(next.generation_in_progress) && next.generation_phase === 'publishing'
    && Boolean(previous.updated_at)
    && previous.updated_at === next.updated_at && previous.version === next.version
    && previous.preview_revision === next.preview_revision
    && (!previous.generation_run_id || previous.generation_run_id === next.generation_run_id);
}

export function mergeDesignPageMetadata(previous: DesignPage, next: DesignPage): DesignPage {
  if (isOlderRevision(previous, next) || isStalePublication(previous, next)) return { ...previous };

  if (previous.version !== next.version) return { ...next };

  const hasIncomingContent = Boolean(next.compiled_html || next.jsx_content);
  const loadedRevision = hasIncomingContent
    ? next.loaded_content_revision || designPageRevision(next)
    : previous.loaded_content_revision || designPageRevision(previous);

  return {
    ...previous,
    ...next,
    loaded_content_revision: loadedRevision,
    ...(!next.compiled_html && next.has_compiled_html && previous.compiled_html
      ? { compiled_html: previous.compiled_html } : {}),
    ...(!next.jsx_content && next.has_jsx_content && previous.jsx_content
      ? { jsx_content: previous.jsx_content } : {}),
    ...(next.section_plan_json === undefined
      ? { section_plan_json: previous.section_plan_json } : {}),
    ...(next.section_sources_json === undefined
      ? { section_sources_json: previous.section_sources_json } : {}),
  };
}

export function mergeDesignPagesPreservingLoadedContent(
  previousPages: DesignPage[],
  nextPages: DesignPage[],
): DesignPage[] {
  const previousById = new Map(previousPages.map((page) => [page.id, page]));

  return nextPages.map((page) => {
    const previous = previousById.get(page.id);

    return previous ? mergeDesignPageMetadata(previous, page) : page;
  });
}

export function mergeDesignPagesFromSse(
  previousPages: DesignPage[],
  incomingPages: DesignPage[],
): DesignPage[] {
  const mergedRealPages = mergeDesignPagesPreservingLoadedContent(previousPages, incomingPages);
  const incomingNames = new Set(incomingPages.map((page) => page.page_name));
  const pendingPlannedPages = previousPages.filter((page) => (
    page.id.startsWith('virtual:') && !incomingNames.has(page.page_name)
  ));

  return [...mergedRealPages, ...pendingPlannedPages];
}
